import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import type { RecipientStatus } from '@/types/referral-outbound-center/models/RecipientStatus';
import { RecipientStatus as RecipientStatusEnum } from '@/types/referral-outbound-center/models/RecipientStatus';

dayjs.extend(utc);

export function formatDob(dob: Date | null | undefined): string {
  if (!dob || Number.isNaN(dob.getTime())) {
    return '—';
  }
  return dayjs(dob).format('MMM D, YYYY');
}

export function computeAge(dob: Date | null | undefined): number | null {
  if (!dob || Number.isNaN(dob.getTime())) {
    return null;
  }
  const age = dayjs().diff(dayjs(dob), 'year');
  return age >= 0 && age < 150 ? age : null;
}

export function formatDateShort(
  value: Date | string | null | undefined
): string | null {
  if (!value) {
    return null;
  }
  const parsed = dayjs.utc(value);
  return parsed.isValid() ? parsed.format('MMM D, YYYY') : null;
}

export type StatusTone = 'neutral' | 'info' | 'success' | 'warning' | 'error';

export type JourneyStepState = 'complete' | 'current' | 'upcoming';

export interface JourneyStep {
  key: string;
  label: string;
  state: JourneyStepState;
  tone?: StatusTone;
}

const TERMINAL_STATUSES: RecipientStatus[] = [
  RecipientStatusEnum.Accepted,
  RecipientStatusEnum.Confirmed,
  RecipientStatusEnum.Declined,
  RecipientStatusEnum.Closed
];

const DECISION_LABELS: Partial<Record<RecipientStatus, string>> = {
  [RecipientStatusEnum.Accepted]: 'Accepted',
  [RecipientStatusEnum.Confirmed]: 'Confirmed',
  [RecipientStatusEnum.Declined]: 'Declined',
  [RecipientStatusEnum.Closed]: 'Closed'
};

const DECISION_TONES: Partial<Record<RecipientStatus, StatusTone>> = {
  [RecipientStatusEnum.Accepted]: 'success',
  [RecipientStatusEnum.Confirmed]: 'success',
  [RecipientStatusEnum.Declined]: 'error',
  [RecipientStatusEnum.Closed]: 'neutral'
};

export function buildJourneySteps(status: RecipientStatus): JourneyStep[] {
  const isTerminal = TERMINAL_STATUSES.includes(status);
  return [
    { key: 'sent', label: 'Sent', state: 'complete' },
    { key: 'received', label: 'Received', state: 'complete' },
    {
      key: 'review',
      label: 'Under Review',
      state: isTerminal ? 'complete' : 'current'
    },
    {
      key: 'decision',
      label: DECISION_LABELS[status] ?? 'Decision',
      state: isTerminal ? 'current' : 'upcoming',
      tone: isTerminal ? DECISION_TONES[status] : undefined
    }
  ];
}

export const REFERRAL_TYPE_LABELS: Record<string, string> = {
  home_health: 'Home Health',
  snf: 'Skilled Nursing',
  ltac: 'Long-Term Acute Care',
  dme: 'DME',
  dialysis: 'Dialysis'
};

export function getReferralTypeLabel(
  type: string | null | undefined
): string | null {
  if (!type) {
    return null;
  }
  return REFERRAL_TYPE_LABELS[type] ?? type;
}
